import React from 'react'

const MFA_EMAIL_SEND_FAILED_MESSAGE = (
  <>
    We couldn&apos;t email you the verification code. Ask your administrator for
    a code or click <strong>Send code</strong> to try again.
  </>
)

/** Maps edu-api MFA route `message` keys to user-facing copy for login/resend/verify. */

const MFA_LOGIN_ERRORS = {
  mfa_session_required:
    'Sign in with your password to start verification and receive a new code.',
  invalid_mfa_session: 'Your session expired. Sign in again to get a new code.',
  user_email_not_found:
    'We could not send a verification code to your email. Please contact your administrator.',
  mfa_code_not_sent:
    'No verification code is active. Use Resend Code or sign in again with your password.',
  mfa_code_expired:
    'Your verification code has expired. Use Resend Code to get a new one.',
  mfa_email_send_failed: MFA_EMAIL_SEND_FAILED_MESSAGE,
  mfa_email_not_configured:
    'Verification email is not available in this environment. Ask your administrator for a verification code.',
  too_many_resend_attempts: 'Too many resend attempts. Please try again later.',
  too_many_verify_attempts:
    'Too many verification attempts. Please try again later.',
}

export const getMfaApiErrorKey = (err) =>
  err?.response?.data?.message || err?.message || err?.data?.message || ''

export const getLoginMfaUserMessage = (err, defaults) => {
  const apiKey = getMfaApiErrorKey(err)
  if (apiKey && MFA_LOGIN_ERRORS[apiKey]) {
    return MFA_LOGIN_ERRORS[apiKey]
  }
  if (err?.status === 429) {
    return defaults.tooManyAttempts
  }
  if (err?.status >= 500) {
    return defaults.serverError
  }
  return defaults.generic
}

export const isInvalidMfaCodeError = (err) =>
  err?.status === 400 && getMfaApiErrorKey(err) === 'invalid_mfa_code'

export const MFA_SESSION_EXPIRED_ERROR_KEYS = new Set([
  'invalid_mfa_session',
  'mfa_session_required',
])

/** sendError / API message keys that map to Segment `mfaActions: EmailBounce`. */
export const MFA_EMAIL_BOUNCE_ERROR_KEYS = new Set([
  'user_email_not_found',
  'mfa_email_send_failed',
])

/** sendError / API message keys that map to Segment `mfaActions: GenerationFailed`. */
export const MFA_GENERATION_FAILED_ERROR_KEYS = new Set([
  'mfa_session_save_failed',
  'mfa_email_not_configured',
  'no_active_mfa_session',
  ...MFA_SESSION_EXPIRED_ERROR_KEYS,
])

/** API message keys that map to Segment `mfaActions: LimitReached`. */
export const MFA_LIMIT_REACHED_ERROR_KEYS = new Set([
  'too_many_verify_attempts',
  'too_many_resend_attempts',
  'too_many_admin_generate_attempts',
])

export const isMfaSessionExpiredError = (err) =>
  err?.status === 400 &&
  MFA_SESSION_EXPIRED_ERROR_KEYS.has(getMfaApiErrorKey(err))

export const isTooManyVerifyAttemptsError = (err) =>
  err?.status === 429 && getMfaApiErrorKey(err) === 'too_many_verify_attempts'

export const isTooManyResendAttemptsError = (err) =>
  err?.status === 429 && getMfaApiErrorKey(err) === 'too_many_resend_attempts'

const MFA_EMAIL_SEND_FAILED_ALERTS = {
  user_email_not_found:
    'There is no email address on your account, so we cannot send a verification code. Contact your administrator or ask them for a verification code.',
  mfa_email_not_configured:
    'Verification email is not available in this environment. Ask your administrator for a verification code.',
  mfa_session_save_failed:
    'We could not start verification. Sign in again with your password, or ask your administrator for a verification code.',
}

export const getMfaEmailSendFailedAlertMessage = (sendError) =>
  MFA_EMAIL_SEND_FAILED_ALERTS[sendError] || MFA_EMAIL_SEND_FAILED_MESSAGE
